import type { FC } from 'react';
import { useEffect } from 'react';

import { EmptyStateView } from '../../components/EmptyStateView';
import { LoadingView } from '../../components/LoadingView';
import type { CompanyOverviewProps } from '../../domain/company';
import type { WidgetItem } from '../../domain/company_widgets';
import { useCompanyQuery } from '../../features/company/useCompanyQuery';
import { invalidateCompanyQuotaQuery, useCompanyQuotaQuery } from '../../features/company/useCompanyQuotaQuery';
import { invalidateDataCreditsQuery } from '../../features/quota/useDataCreditsQuery';
import { COMPANY_OVERVIEW_WIDGETS_CONFIG } from '../../services/companyWidgets';
import { StyledContainerWithNav } from '../styles';

import { CompanyWidget } from './CompanyWidget';
import { StyledCompanyOverview } from './styles';
import { useCompanyOverviewWidgets } from './useCompanyOverviewWidgets';
import { WidgetPicker } from './WidgetPicker';
import { useWidgetPickerTracking } from './WidgetPicker/useWidgetPickerTracking';

export const CompanyOverview: FC<CompanyOverviewProps> = ({ variant = 'page', onScrollToTop }) => {
  const companyQuery = useCompanyQuery();
  const companyQuota = useCompanyQuotaQuery();
  const company = companyQuery.data;

  const { allWidgets, selectedWidgets, onSaveWidgets } = useCompanyOverviewWidgets();
  const { trackHoverWidget } = useWidgetPickerTracking();

  const handleSaveWidgets = (widgets: WidgetItem[]) => {
    onScrollToTop();
    onSaveWidgets(widgets);
  };

  useEffect(() => {
    if (!companyQuota?.data || companyQuota?.data.isUnlimited === false) {
      invalidateCompanyQuotaQuery();
      invalidateDataCreditsQuery();
    }
  }, [company?.domain, companyQuota?.data]);

  if (companyQuery.isLoading) {
    return <LoadingView />;
  }

  if (!company || companyQuery.isError) {
    return (
      <StyledContainerWithNav data-automation="CompanyEmptyState">
        <EmptyStateView
          messageKey="company.empty_state.message_linkedin"
          img="/images/empty-state-lens.svg"
          variant={variant === 'page' ? 'lens' : 'lens-rised'}
        />
      </StyledContainerWithNav>
    );
  }

  return (
    <StyledCompanyOverview>
      <div onMouseEnter={() => trackHoverWidget('overview')}>
        <CompanyWidget company={company} />
      </div>
      {selectedWidgets.map(({ id }) => {
        const WidgetComponent = COMPANY_OVERVIEW_WIDGETS_CONFIG[id].component;
        return (
          <div key={id} onMouseEnter={() => trackHoverWidget(id)}>
            <WidgetComponent company={company} />
          </div>
        );
      })}
      <WidgetPicker widgets={allWidgets} onSaveWidgets={handleSaveWidgets} />
    </StyledCompanyOverview>
  );
};
